import log from "@/helpers/log";

/**
 * Marketing attribution capture. Scope is UTM parameters only — no ad click
 * ids, referrer, or landing page.
 *
 * UTM tags only exist in the URL for the moment the user lands, so they are
 * captured immediately and parked in localStorage — which must survive weeks
 * and tab closure, unlike the sessionStorage login-query stash that carries
 * `invite_code` through Auth0 and is cleared straight after login.
 *
 * The parked copy is only a holding pen. Once the user signs up the data is
 * persisted on the user row (see attributionPersistence.ts) and read back from
 * the API from then on, which is what makes a conversion in a different browser
 * still attributable.
 */

const STORAGE_KEY = "web3auth_attribution";
const SYNCED_KEY = "web3auth_attribution_synced_at";
const SCHEMA_VERSION = 1;

// Bounds mirror the server-side limits in backend/pkg/auth/attribution.go.
const MAX_UTM_PARAMS = 10;
/** Longest key UTM_KEY_PATTERN accepts: "utm_" plus 30 characters. */
const MAX_KEY_LENGTH = 34;
const MAX_VALUE_LENGTH = 255;
const ATTRIBUTION_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * `at` is the one field a lenient Date.parse would not bound: V8's legacy date
 * parser accepts an arbitrarily long trailing parenthesised comment, so a
 * crafted value would pass the window check and inflate the signup request body
 * past the server's 1MB limit, failing the signup. Requiring ISO-8601 also
 * matches the backend's strict RFC3339 parse, so a value we accept is a value
 * the server will keep.
 */
const AT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;
const MAX_AT_LENGTH = 40;

function isValidTouchTime(value: unknown): value is string {
  return typeof value === "string" && value.length <= MAX_AT_LENGTH && AT_PATTERN.test(value);
}

/**
 * Any utm_* parameter is captured, so a campaign launched with a brand new tag
 * is collected before anyone asks for it. Keys are kept under their incoming
 * name with no renaming: utm_id and utm_campaignid are distinct
 * campaign-identifier parameters, not aliases of each other.
 */
const UTM_KEY_PATTERN = /^utm_[a-z0-9_]{1,30}$/;

/**
 * Parameters sent to Segment/Mixpanel as named properties. Anything captured
 * but not listed here is collapsed into a single `utm_extra` property, so a
 * typo (`utm_souce`) or a crafted URL cannot permanently pollute the Mixpanel
 * lexicon. The raw values stay in the DB, so promoting a key here later can be
 * backfilled.
 */
const REPORTED_UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_id", "utm_campaignid", "utm_ad_group", "utm_term", "utm_content"];

export type AttributionTouch = {
  utm?: Record<string, string>;
  at: string;
};

export type UserAttribution = {
  initial?: AttributionTouch;
  latest?: AttributionTouch;
  v?: number;
};

/**
 * Truncates and strips characters that have no business in a campaign tag.
 * These values are attacker-controlled and end up in the GTM dataLayer, where a
 * Custom HTML tag writing a dataLayer variable into the DOM would turn
 * `utm_source=<img onerror=...>` into stored XSS. Cheap to defuse here.
 */
function clean(value: string, max = MAX_VALUE_LENGTH): string {
  // Markup-significant and control characters only. Spaces, hyphens and
  // underscores are legitimate in campaign names and must survive.
  /* eslint-disable-next-line no-control-regex */
  const stripped = value.replace(/[<>"'`\\]|[\u0000-\u001f\u007f]/g, "").trim();
  return stripped.length > max ? stripped.slice(0, max) : stripped;
}

function normalizeUtmKey(key: string): string | undefined {
  const lowered = key.trim().toLowerCase();
  if (!lowered || lowered.length > MAX_KEY_LENGTH) return undefined;
  return UTM_KEY_PATTERN.test(lowered) ? lowered : undefined;
}

/** Builds a touch from the current URL, or undefined when there is no campaign signal to record. */
function readTouchFromUrl(): AttributionTouch | undefined {
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};

  params.forEach((rawValue, rawKey) => {
    const value = clean(rawValue);
    if (!value) return;

    const utmKey = normalizeUtmKey(rawKey);
    if (utmKey && Object.keys(utm).length < MAX_UTM_PARAMS) utm[utmKey] = value;
  });

  // A direct visit carries no campaign signal and must never overwrite a
  // stored touch — otherwise every returning visitor looks like they came from
  // nowhere.
  if (!Object.keys(utm).length) return undefined;

  return { utm, at: new Date().toISOString() };
}

function isWithinWindow(touch: AttributionTouch | undefined): boolean {
  if (!isValidTouchTime(touch?.at)) return false;
  const touchedAt = Date.parse(touch.at);
  const age = Date.now() - touchedAt;
  return Number.isFinite(touchedAt) && age >= 0 && age <= ATTRIBUTION_WINDOW_MS;
}

function readStorage(): UserAttribution | undefined {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as UserAttribution) : undefined;
  } catch (error) {
    // Private mode, blocked storage, or a corrupt blob: treat as absent.
    log.debug("unable to read stored attribution", error);
    return undefined;
  }
}

/**
 * Captures the current URL's campaign parameters. The initial touch is only set
 * once; the latest touch is overwritten on every tagged visit.
 */
export function captureAttribution(): void {
  const touch = readTouchFromUrl();
  if (!touch) return;

  const stored = readStorage();
  const next: UserAttribution = {
    initial: isWithinWindow(stored?.initial) ? stored?.initial : touch,
    latest: touch,
    v: SCHEMA_VERSION,
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    log.debug("unable to store attribution", error);
  }
}

/**
 * Rebuilds a touch from a whitelist of fields with their types checked, rather
 * than trusting the parsed blob. The blob is attacker-writable (XSS, an
 * extension, a stale schema from a previous release) and is sent verbatim in
 * the signup request body, where a wrong field type would fail the whole
 * request decode and block account creation.
 */
function rebuildTouch(value: unknown): AttributionTouch | undefined {
  if (!value || typeof value !== "object") return undefined;
  const raw = value as Record<string, unknown>;
  if (!isValidTouchTime(raw.at)) return undefined;

  const rebuildMap = (input: unknown, allowKey: (key: string) => string | undefined) => {
    if (!input || typeof input !== "object") return undefined;
    const result: Record<string, string> = {};
    Object.entries(input as Record<string, unknown>).forEach(([key, entry]) => {
      if (typeof entry !== "string" || Object.keys(result).length >= MAX_UTM_PARAMS) return;
      const allowed = allowKey(key);
      const cleaned = clean(entry);
      if (allowed && cleaned) result[allowed] = cleaned;
    });
    return Object.keys(result).length ? result : undefined;
  };

  const touch: AttributionTouch = { at: raw.at };
  const utm = rebuildMap(raw.utm, normalizeUtmKey);
  if (utm) touch.utm = utm;

  // No campaign signal left means nothing worth sending.
  return touch.utm ? touch : undefined;
}

/** Returns the parked attribution, dropping touches outside the attribution window. */
export function getStoredAttribution(): UserAttribution | undefined {
  const stored = readStorage();
  // A blob from a different schema version is discarded rather than guessed at.
  if (!stored || stored.v !== SCHEMA_VERSION) return undefined;

  const attribution: UserAttribution = { v: SCHEMA_VERSION };
  const initial = rebuildTouch(stored.initial);
  const latest = rebuildTouch(stored.latest);
  if (isWithinWindow(initial)) attribution.initial = initial;
  if (isWithinWindow(latest)) attribution.latest = latest;

  return attribution.initial || attribution.latest ? attribution : undefined;
}

function syncMarker(attribution: UserAttribution): string {
  return attribution.latest?.at ?? attribution.initial?.at ?? "";
}

/** True when the parked latest touch has already been sent to the backend, so logins don't re-PATCH on every page load. */
export function isSynced(attribution: UserAttribution): boolean {
  const marker = syncMarker(attribution);
  if (!marker) return false;
  try {
    return window.localStorage.getItem(SYNCED_KEY) === marker;
  } catch {
    return false;
  }
}

export function markSynced(attribution: UserAttribution): void {
  const marker = syncMarker(attribution);
  if (!marker) return;
  try {
    window.localStorage.setItem(SYNCED_KEY, marker);
  } catch (error) {
    log.debug("unable to mark attribution as synced", error);
  }
}

/**
 * Clears the synced marker. Called on logout so the next user in this browser
 * is not treated as already synced, which would silently drop their
 * attribution.
 */
export function clearAttributionSyncMarker(): void {
  try {
    window.localStorage.removeItem(SYNCED_KEY);
  } catch (error) {
    log.debug("unable to clear attribution sync marker", error);
  }
}

/**
 * First-touch properties, sourced from the user's row via the API rather than
 * localStorage so they resolve on a device that never saw the ad click. This is
 * what makes a cross-browser conversion attributable.
 *
 * These are attached to every *event* rather than set as a Mixpanel profile
 * property, for two reasons. First, both Segment sources in this app are
 * entity-keyed — `orgAnalytics` by team id, `projectAnalytics` by project id —
 * so there is no user-keyed source whose profile could hold them. Second, event
 * properties are frozen at fire time, while a breakdown by a profile property
 * uses its *current* value with no history and would silently re-attribute past
 * events.
 */
export function toInitialTouchProperties(attribution: UserAttribution | undefined): Record<string, string> {
  const initial = attribution?.initial;
  if (!initial) return {};

  const properties: Record<string, string> = {};
  REPORTED_UTM_KEYS.forEach((key) => {
    const value = initial.utm?.[key];
    if (value) properties[`initial_${key}`] = value;
  });
  if (initial.at) properties.initial_touch_at = initial.at;

  return properties;
}

/**
 * Latest-touch properties, read from this browser's parked copy since they
 * describe the current session.
 */
export function toLatestTouchProperties(): Record<string, string> {
  const latest = getStoredAttribution()?.latest;
  if (!latest) return {};

  const properties: Record<string, string> = {};
  const extra: string[] = [];

  Object.entries(latest.utm ?? {}).forEach(([key, value]) => {
    if (REPORTED_UTM_KEYS.includes(key)) properties[key] = value;
    else extra.push(`${key}=${value}`);
  });
  if (extra.length) properties.utm_extra = clean(extra.join("&"));

  return properties;
}

/**
 * Bare utm_* key/value pairs for the GTM virtual-page URL query — no
 * `initial_`/`latest_` prefix, since these become literal query parameters
 * GTM's History Change trigger reads off the URL.
 *
 * Best-effort per key: prefers this browser's latest (session) touch, then
 * the server-synced latest touch (covers a device with no local storage but
 * a prior synced session elsewhere), then falls back to the server-sourced
 * initial touch, so a conversion happening in a browser that never saw the
 * ad click still carries the acquiring campaign in the URL.
 */
export function toUtmQueryParams(profileAttribution: UserAttribution | undefined): Record<string, string> {
  const initialUtm = profileAttribution?.initial?.utm ?? {};
  const profileLatestUtm = profileAttribution?.latest?.utm ?? {};
  const localLatestUtm = getStoredAttribution()?.latest?.utm ?? {};

  const params: Record<string, string> = {};
  REPORTED_UTM_KEYS.forEach((key) => {
    const value = localLatestUtm[key] ?? profileLatestUtm[key] ?? initialUtm[key];
    if (value) params[key] = value;
  });

  return params;
}
